import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  ImagePlus,
  Loader2,
  RefreshCw,
  Trash2,
} from "lucide-react";
import AppShell from "../components/AppShell";
import ApiService from "../services/ApiService";
import IntegrationsService from "../services/IntegrationsService";
import PostService from "../services/PostService";
import { cn } from "../components/ui/cn";

const STATUSES = ["draft", "scheduled", "publishing", "published", "failed", "cancelled"];

const STATUS_CLASS = {
  draft: "bg-slate-100 text-slate-600",
  scheduled: "bg-indigo-50 text-indigo-700",
  publishing: "bg-amber-50 text-amber-700",
  published: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  cancelled: "bg-slate-100 text-slate-500",
};

function errorText(err) {
  return err?.response?.data?.message || err?.message || "Something went wrong";
}

function pad(value) {
  return String(value).padStart(2, "0");
}

function toLocalInput(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function monthMatrix(cursor) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function dayKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export default function PublishView() {
  const navigate = useNavigate();
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const [account, setAccount] = useState(null);
  const [pages, setPages] = useState([]);
  const [instagram, setInstagram] = useState([]);
  const [youtube, setYoutube] = useState([]);
  const [posts, setPosts] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [ytTitle, setYtTitle] = useState("");
  const [ytIsShort, setYtIsShort] = useState(false);
  const [files, setFiles] = useState([]);
  const [keptMedia, setKeptMedia] = useState([]);
  const [fbId, setFbId] = useState("");
  const [igId, setIgId] = useState("");
  const [ytId, setYtId] = useState("");
  const [mode, setMode] = useState("now");
  const [when, setWhen] = useState(() => toLocalInput(new Date(Date.now() + 60 * 60 * 1000)));
  const [repeatDaily, setRepeatDaily] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [slotTime, setSlotTime] = useState("18:30");
  const [slotFb, setSlotFb] = useState("");
  const [slotIg, setSlotIg] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const [me, connected, postRows, slotRows] = await Promise.all([
        ApiService.getAccountMe(),
        IntegrationsService.listConnected({ includePaused: true }).catch(() => []),
        PostService.list(),
        PostService.listSlots(),
      ]);
      setAccount(me?.account ?? null);
      const rows = Array.isArray(connected) ? connected : [];
      setPages(rows.filter((row) => row.providerAccountKind === "facebook_page" && row.status !== "disconnected"));
      setInstagram(rows.filter((row) => row.providerAccountKind === "instagram_business" && row.status !== "disconnected"));
      setYoutube(rows.filter((row) => row.providerAccountKind === "youtube_channel" && row.status !== "disconnected"));
      setPosts(Array.isArray(postRows) ? postRows : []);
      setSlots(Array.isArray(slotRows) ? slotRows : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/signin");
        return;
      }
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const resetComposer = () => {
    setCaption("");
    setYtTitle("");
    setYtIsShort(false);
    setFiles([]);
    setKeptMedia([]);
    setFbId("");
    setIgId("");
    setYtId("");
    setMode("now");
    setRepeatDaily(false);
    setEditingId(null);
  };

  const uploadSelected = async () => {
    const uploaded = [];
    for (const file of files) {
      const media = await PostService.upload(file);
      if (file.type?.startsWith("video/") && typeof document !== "undefined") {
        try {
          const objectUrl = URL.createObjectURL(file);
          const meta = await new Promise((resolve) => {
            const el = document.createElement("video");
            el.preload = "metadata";
            el.onloadedmetadata = () => {
              resolve({
                width: el.videoWidth || null,
                height: el.videoHeight || null,
                duration: Number.isFinite(el.duration) ? Math.round(el.duration) : null,
              });
              URL.revokeObjectURL(objectUrl);
            };
            el.onerror = () => {
              resolve({});
              URL.revokeObjectURL(objectUrl);
            };
            el.src = objectUrl;
          });
          Object.assign(media, meta);
        } catch {
          /* dimensions optional */
        }
      }
      uploaded.push(media);
    }
    return [...keptMedia, ...uploaded];
  };

  const composerBody = (media) => ({
    caption,
    fbConnectedAccountId: ytId ? undefined : (fbId || undefined),
    igConnectedAccountId: ytId ? undefined : (igId || undefined),
    ytConnectedAccountId: ytId || undefined,
    ytTitle: ytId ? (ytTitle || undefined) : undefined,
    ytIsShort: ytId ? ytIsShort : undefined,
    ...(media ? { media } : {}),
  });

  const saveDraft = async () => {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const media = files.length || keptMedia.length ? await uploadSelected() : undefined;
      const body = composerBody(media);
      if (editingId) await PostService.update(editingId, body);
      else await PostService.create(body);
      setNotice(editingId ? "Draft updated." : "Draft saved.");
      resetComposer();
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const media = files.length || keptMedia.length ? await uploadSelected() : undefined;
      const body = composerBody(media);
      let id = editingId;
      if (id) await PostService.update(id, body);
      else {
        const created = await PostService.create(body);
        id = created.id;
      }
      if (mode === "now") await PostService.publish(id);
      else {
        await PostService.schedule(id, {
          scheduledAt: new Date(when).toISOString(),
          timezone,
          repeatDaily: ytId ? false : repeatDaily,
        });
      }
      setNotice(mode === "now" ? "Publish requested." : "Scheduled.");
      resetComposer();
      await load();
    } catch (err) {
      setError(errorText(err));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const editPost = (post) => {
    setEditingId(post.id);
    setCaption(post.caption || "");
    setYtTitle(post.ytTitle || post.targets?.ytTitle || "");
    setYtIsShort(Boolean(post.targets?.ytIsShort));
    setFiles([]);
    setKeptMedia(post.media || []);
    setFbId(post.targets?.fbConnectedAccountId || "");
    setIgId(post.targets?.igConnectedAccountId || "");
    setYtId(post.targets?.ytConnectedAccountId || "");
    if (post.scheduledAt) {
      setMode("schedule");
      setWhen(toLocalInput(new Date(post.scheduledAt)));
    } else {
      setMode("now");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const days = monthMatrix(month);
  const byDay = useMemo(() => {
    const map = {};
    posts.forEach((post) => {
      const stamp = post.scheduledAt || post.publishedAt;
      if (!stamp) return;
      const key = dayKey(new Date(stamp));
      map[key] = map[key] || [];
      map[key].push(post);
    });
    return map;
  }, [posts]);

  const signOut = async () => {
    try { await ApiService.signout(); } catch { /* session already gone */ }
    navigate("/signin");
  };

  return (
    <AppShell account={account} onSignOut={signOut} title="Publish">
      <div className="mx-auto max-w-6xl space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}
        {notice && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{editingId ? "Edit post" : "New post"}</h2>
              <p className="text-sm text-slate-500">Times use {timezone} and are stored as UTC. Facebook and YouTube use native scheduling. Instagram publishes from this server when due.</p>
            </div>
            {editingId && (
              <button type="button" className="text-sm text-slate-500 underline" onClick={resetComposer}>Clear</button>
            )}
          </div>
          <textarea
            className="min-h-28 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800"
            placeholder={ytId ? "Description (caption)" : "Caption"}
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
          />
          {ytId && (
            <input
              className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800"
              placeholder="YouTube title"
              maxLength={100}
              value={ytTitle}
              onChange={(event) => setYtTitle(event.target.value)}
            />
          )}
          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
            <ImagePlus className="h-4 w-4" />
            <span>{ytId ? "Add video (required for YouTube)" : "Add image or video"}</span>
            <input
              className="sr-only"
              type="file"
              accept={ytId ? "video/mp4,video/quicktime" : "image/jpeg,image/png,video/mp4,video/quicktime"}
              multiple={!ytId}
              onChange={(event) => setFiles(Array.from(event.target.files || []).slice(0, ytId ? 1 : undefined))}
            />
          </label>
          <ul className="mt-2 space-y-1 text-xs text-slate-500">
            {keptMedia.map((item) => <li key={item.id || item.url}>{item.type} · already uploaded</li>)}
            {files.map((file) => <li key={file.name}>{file.name}</li>)}
          </ul>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <label className="text-sm text-slate-600">
              Facebook Page
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={fbId}
                disabled={Boolean(ytId)}
                onChange={(event) => { setFbId(event.target.value); if (event.target.value) setYtId(""); }}
              >
                <option value="">Do not post to Facebook</option>
                {pages.map((page) => <option key={page.id} value={page.id}>{page.displayName || page.providerAccountId}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              Instagram
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={igId}
                disabled={Boolean(ytId)}
                onChange={(event) => { setIgId(event.target.value); if (event.target.value) setYtId(""); }}
              >
                <option value="">Do not post to Instagram</option>
                {instagram.map((row) => <option key={row.id} value={row.id}>{row.displayName || row.providerAccountId}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              YouTube
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={ytId}
                onChange={(event) => {
                  setYtId(event.target.value);
                  if (event.target.value) {
                    setFbId("");
                    setIgId("");
                  }
                }}
              >
                <option value="">Do not post to YouTube</option>
                {youtube.map((row) => <option key={row.id} value={row.id}>{row.displayName || row.providerAccountId}</option>)}
              </select>
            </label>
          </div>
          {ytId && (
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={ytIsShort} onChange={(event) => setYtIsShort(event.target.checked)} />
              Upload as YouTube Short (vertical/square, ≤ 3 min; adds #Shorts)
            </label>
          )}
          {!pages.length && !instagram.length && !youtube.length && !loading && (
            <p className="mt-3 text-sm text-amber-700">Connect a Facebook Page, Instagram account, or YouTube channel under Data sources before publishing.</p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" checked={mode === "now"} onChange={() => setMode("now")} />
              Publish now
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={mode === "schedule"} onChange={() => setMode("schedule")} />
              Schedule
            </label>
            {mode === "schedule" && (
              <>
                <input
                  type="datetime-local"
                  className="rounded-lg border border-slate-200 px-3 py-2"
                  value={when}
                  onChange={(event) => setWhen(event.target.value)}
                />
                {!ytId && (
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={repeatDaily} onChange={(event) => setRepeatDaily(event.target.checked)} />
                    Repeat daily for up to 75 days
                  </label>
                )}
              </>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={submit}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy ? "Working…" : mode === "now" ? "Publish now" : "Schedule"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={saveDraft}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            >
              Save draft
            </button>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <CalendarDays className="h-5 w-5" />
              Calendar
            </h2>
            <div className="flex items-center gap-2 text-sm">
              <button type="button" className="rounded border px-2 py-1" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>Prev</button>
              <span className="font-medium text-slate-700">{month.toLocaleString(undefined, { month: "long", year: "numeric" })}</span>
              <button type="button" className="rounded border px-2 py-1" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>Next</button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-400">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => <div key={label}>{label}</div>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {days.map((day) => {
              const key = dayKey(day);
              const items = byDay[key] || [];
              const inMonth = day.getMonth() === month.getMonth();
              return (
                <div key={key} className={cn("min-h-16 rounded-lg border p-1", inMonth ? "border-slate-100 bg-white" : "border-transparent bg-slate-50 text-slate-300")}>
                  <div className="text-xs">{day.getDate()}</div>
                  {items.slice(0, 2).map((post) => (
                    <div key={post.id} className={cn("mt-1 truncate rounded px-1 text-[10px]", STATUS_CLASS[post.status] || STATUS_CLASS.draft)}>
                      {post.status}
                    </div>
                  ))}
                  {items.length > 2 && <div className="text-[10px] text-slate-400">+{items.length - 2}</div>}
                </div>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Posts</h2>
            <button type="button" onClick={() => { setLoading(true); load(); }} className="inline-flex items-center gap-1 text-sm text-slate-500">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
          {loading && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading posts…</div>}
          {!loading && posts.length === 0 && <p className="text-sm text-slate-500">No posts yet. Save a draft or schedule one above.</p>}
          <ul className="divide-y divide-slate-100">
            {posts.map((post) => (
              <li key={post.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", STATUS_CLASS[post.status] || STATUS_CLASS.draft)}>
                      {STATUSES.includes(post.status) ? post.status : post.status}
                    </span>
                    <span className="text-xs text-slate-400">{formatWhen(post.scheduledAt || post.publishedAt)}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-800">{post.caption || "No caption"}</p>
                  <p className="text-xs text-slate-500">
                    {post.targets?.fbPageId ? "Facebook " : ""}
                    {post.targets?.igUserId ? "Instagram " : ""}
                    {post.targets?.ytChannelId ? "YouTube " : ""}
                    {post.fbPostId ? `· FB ${post.fbPostId}` : ""}
                    {post.igMediaId ? `· IG ${post.igMediaId}` : ""}
                    {post.ytVideoId ? `· YT ${post.ytVideoId}` : ""}
                    {post.permalink ? ` · ${post.permalink}` : ""}
                    {post.retries ? `· retries ${post.retries}` : ""}
                  </p>
                  {post.error && <p className="mt-1 text-xs text-red-600">{post.error}</p>}
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {["draft", "scheduled", "failed"].includes(post.status) && (
                    <button type="button" className="rounded border px-2 py-1" onClick={() => editPost(post)}>Edit</button>
                  )}
                  {post.status === "failed" && (
                    <button type="button" className="rounded border px-2 py-1" disabled={busy} onClick={() => act(() => PostService.retry(post.id))}>Retry</button>
                  )}
                  {post.status === "scheduled" && (
                    <button type="button" className="rounded border px-2 py-1" disabled={busy} onClick={() => act(() => PostService.cancel(post.id))}>Cancel</button>
                  )}
                  <button type="button" className="rounded border px-2 py-1 text-red-600" disabled={busy} onClick={() => act(() => PostService.remove(post.id))}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Daily slots</h2>
          <p className="mb-3 text-sm text-slate-500">A slot is a clock time in {timezone}. Scheduling a post onto that time, with repeat daily, creates Facebook native scheduled posts up to 75 days ahead. Instagram rows wait for the minute publisher.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-slate-600">
              Time
              <input type="time" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2" value={slotTime} onChange={(event) => setSlotTime(event.target.value)} />
            </label>
            <label className="text-sm text-slate-600">
              Facebook
              <select className="mt-1 block rounded-lg border border-slate-200 px-3 py-2" value={slotFb} onChange={(event) => setSlotFb(event.target.value)}>
                <option value="">None</option>
                {pages.map((page) => <option key={page.id} value={page.id}>{page.displayName || "Page"}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-600">
              Instagram
              <select className="mt-1 block rounded-lg border border-slate-200 px-3 py-2" value={slotIg} onChange={(event) => setSlotIg(event.target.value)}>
                <option value="">None</option>
                {instagram.map((row) => <option key={row.id} value={row.id}>{row.displayName || "Instagram"}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
              disabled={busy}
              onClick={() => act(async () => {
                await PostService.createSlot({
                  timeOfDay: slotTime,
                  timezone,
                  fbConnectedAccountId: slotFb || undefined,
                  igConnectedAccountId: slotIg || undefined,
                  active: true,
                });
                setNotice("Daily slot saved.");
              })}
            >
              Add slot
            </button>
          </div>
          {!slots.length && <p className="mt-3 text-sm text-slate-500">No daily slots yet.</p>}
          <ul className="mt-3 divide-y divide-slate-100">
            {slots.map((slot) => (
              <li key={slot.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span>{slot.timeOfDay} · {slot.timezone} · {slot.active ? "active" : "paused"}</span>
                <span className="flex gap-2">
                  <button
                    type="button"
                    className="rounded border px-2 py-1 text-xs"
                    onClick={() => {
                      setMode("schedule");
                      const [hour, minute] = slot.timeOfDay.split(":");
                      const next = new Date();
                      next.setHours(Number(hour), Number(minute), 0, 0);
                      if (next.getTime() < Date.now() + 10 * 60 * 1000) next.setDate(next.getDate() + 1);
                      setWhen(toLocalInput(next));
                      setFbId(slot.targets?.fbConnectedAccountId || "");
                      setIgId(slot.targets?.igConnectedAccountId || "");
                      setRepeatDaily(true);
                    }}
                  >
                    Use in composer
                  </button>
                  <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => act(() => PostService.updateSlot(slot.id, { active: !slot.active }))}>
                    {slot.active ? "Pause" : "Activate"}
                  </button>
                  <button type="button" className="rounded border px-2 py-1 text-xs text-red-600" onClick={() => act(() => PostService.deleteSlot(slot.id))}>Delete</button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AppShell>
  );
}
